import React, { useState } from 'react';
import {
  Form,
  Drawer,
  DrawerProps,
  Upload,
  Avatar,
  message,
  Select,
  Input,
  Button,
  Space,
} from 'antd';
import { useTranslate } from '@tolgee/react';
import { AccountType, updateWabaInfo, uploadFile } from 'services/api/whatsapp';
import styled from '@emotion/styled';
import { useAuthorization } from 'components/Authorization';
import { messDoorClient } from '../../../../services/api';

interface Props extends Omit<DrawerProps, 'onOk'> {
  meta: WhatsappBusinessApi.Info;
  onSuccess?: () => void;
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-footer {
    display: flex;
    flex-direction: row-reverse;
    gap: 12px;
    text-align: left;
    justify-content: flex-end;
  }
`;
const AvatarUpload = styled.div`
  position: relative;
  .mask {
    display: none;
    position: absolute;
    left: 0;
    top: 0;
    width: 108px;
    height: 108px;
    border-radius: 100%;
    background-color: rgba(106, 105, 104, 0.6);
    justify-content: center;
    align-items: center;
    color: white;
  }
  &:hover,
  &.uploading {
    .mask {
      display: flex;
    }
  }
`;
const WhatsappEdit = (props: Props) => {
  const { meta, onSuccess, ...modalProps } = props;
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState(meta?.profilePictureOssUrl);
  const [uploading, setUploading] = useState(false);
  const { token } = useAuthorization();
  const [submiting, setSubmiting] = useState(false);
  const options = Object.values(AccountType).map((value) => ({
    value,
    label: t(`WhatsApp.类型.${value}`),
  }));
  return (
    <StyledDrawer
      title={t('WhatsApp.编辑')}
      width={500}
      maskClosable={false}
      footer={
        <Space>
          <Button
            type="primary"
            loading={submiting}
            onClick={() => {
              form.validateFields().then((value) => {
                setSubmiting(true);
                updateWabaInfo({
                  ...meta,
                  ...value,
                  profilePictureOssUrl: avatar,
                })
                  .then(() => {
                    onSuccess?.();
                    message.success(t('WhatsApp.已提交'));
                  })
                  .catch((info) => {
                    message.error(t('WhatsApp.编辑失败，请重试'));
                  })
                  .finally(() => {
                    setSubmiting(false);
                  });
              });
            }}
          >
            {t('WhatsApp.提交')}
          </Button>
          <Button onClick={onSuccess}>{t('WhatsApp.取消')}</Button>
        </Space>
      }
      {...modalProps}
    >
      <Form
        form={form}
        initialValues={meta}
        labelCol={{
          span: 6,
        }}
      >
        <Form.Item label={t('WhatsApp.table.手机号码')} name="phone">
          {meta.phone}
        </Form.Item>
        <Form.Item label={t('WhatsApp.头像')} name="profilePictureUrl">
          <Upload
            listType="picture"
            accept=".jpg"
            maxCount={1}
            name="file"
            headers={{
              [import.meta.env.VITE_AUTH_HEADER as string]: `Bearer ${token}`,
            }}
            action={`${messDoorClient.getConfig().baseURL}/oversea-wa-biz-home/uploadFile`}
            showUploadList={false}
            disabled={uploading}
            customRequest={(params) => {
              const file = params.file as File;
              if (file.size > 5 * 1024 * 1024) {
                message.error(t('WhatsApp.图片大小超过x限制', { x: 5 }));
                return;
              }

              setUploading(true);
              uploadFile(file, true).then((res) => {
                setUploading(false);
                setAvatar(res.localUrl);
                form.setFieldsValue({
                  profilePictureUrl: res.url,
                });
              });
            }}
          >
            <AvatarUpload className={uploading ? 'uploading' : ''}>
              <Avatar size={108} src={avatar} />
              {avatar || uploading ? (
                <div className="mask">
                  {uploading ? t('WhatsApp.上传中') : t('WhatsApp.重新上传')}
                </div>
              ) : null}
            </AvatarUpload>
          </Upload>
        </Form.Item>
        <Form.Item
          label={t('WhatsApp.table.类型')}
          name="type"
          required
          rules={[
            {
              required: true,
              message: t('WhatsApp.请选择类型'),
            },
          ]}
        >
          <Select options={options} optionFilterProp="label" placeholder={t('WhatsApp.请选择')} />
        </Form.Item>
        <Form.Item
          label={t('WhatsApp.邮箱')}
          name="email"
          rules={[
            {
              type: 'email',
              message: t('WhatsApp.请输入正确的邮箱'),
            },
          ]}
        >
          <Input placeholder={t('WhatsApp.请输入')} />
        </Form.Item>
        <Form.Item label={t('WhatsApp.商家地址')} name="address">
          <Input placeholder={t('WhatsApp.请输入')} />
        </Form.Item>
        <Form.Item label={t('WhatsApp.网址')} name="websites">
          <Input placeholder={t('WhatsApp.请输入')} />
        </Form.Item>
        <Form.Item label={t('WhatsApp.描述')} name="description">
          <Input.TextArea placeholder={t('WhatsApp.请输入')} showCount maxLength={512} />
        </Form.Item>
      </Form>
    </StyledDrawer>
  );
};

export default WhatsappEdit;
